// The fresh authenticator code an action that moves money or changes a capability needs
// (docs/screaming.md, Security 7). The API is the gate: it answers 403 step_up_required, the
// form shows this prompt, and once the code is checked the form sends the same request again.
// It sits in the action's dialog: its buttons are the dialog's footer, and `data-step-up` lets the
// dialog narrow to it (invite-staff/dialog.tsx).
import { useState, type FormEvent } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { FieldGroup } from "@/components/ui/field";
import { ApiError } from "@/lib/api-client";
import { m } from "@/lib/paraglide/messages.js";
import { CodeField } from "./code-field";
import { CODE_MESSAGES, CodeError, useVerifyCode } from "./verify-code";

export const needsStepUp = (error: unknown) => error instanceof ApiError && error.code === "step_up_required";

export function StepUpPrompt({ onConfirmed, onCancel }: { onConfirmed: () => void; onCancel: () => void }) {
  const [code, setCode] = useState("");
  const verify = useVerifyCode();

  function submit(event: FormEvent) {
    event.preventDefault();
    verify.mutate(code, { onSuccess: onConfirmed });
  }

  const reason = verify.error instanceof CodeError ? verify.error.reason : verify.error ? "invalid" : null;
  return (
    <form onSubmit={submit} noValidate data-step-up>
      <FieldGroup>
        <CodeField id="step-up-code" value={code} onChange={setCode} description={m.staff_step_up_prompt()} />
        {reason && (
          <Alert variant="destructive">
            <AlertDescription>{CODE_MESSAGES[reason]()}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {m.common_cancel()}
          </Button>
          <Button type="submit" disabled={verify.isPending || code.length !== 6}>
            {m.staff_step_up_confirm()}
          </Button>
        </DialogFooter>
      </FieldGroup>
    </form>
  );
}
